import React from 'react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import {
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core';
import '../style/NetWorthProjection.css';
import { fetchFinancialAccounts } from '../redux/actions';

class NetWorthProjection extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      currentNetWorth: '',
      currentNetWorthEdited: false,
      growthRate: '7',
      annualRaiseRate: '4',
      monthlySavings: '6000',
      projections: null
    };
  }

  componentDidMount() {
    this.props.dispatch(fetchFinancialAccounts());
    this.syncNetWorthFromAccounts();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.financialAccounts !== this.props.financialAccounts) {
      this.syncNetWorthFromAccounts();
    }
  }

  syncNetWorthFromAccounts = () => {
    if (this.state.currentNetWorthEdited) {
      return;
    }
    const accounts = this.props.financialAccounts;
    if (!accounts || accounts.length === 0) {
      return;
    }
    const netWorth = this.calculateNetWorthFromAccounts(accounts);
    this.setState({ currentNetWorth: String(Math.round(netWorth)) });
  }

  calculateNetWorthFromAccounts = (accounts) => {
    if (!accounts || accounts.length === 0) {
      return 0;
    }
    return accounts.reduce((sum, account) => sum + (account.balance || 0), 0);
  }

  getMonthlySavings = () => parseFloat(this.state.monthlySavings) || 0;

  handleInputChange = (field) => (event) => {
    const updates = { [field]: event.target.value, projections: null };
    if (field === 'currentNetWorth') {
      updates.currentNetWorthEdited = true;
    }
    this.setState(updates);
  }

  getMonthlySavingsForMonth = (monthIndex, baseSavings, raiseRate) => {
    const yearIndex = Math.floor(monthIndex / 12);
    return baseSavings * Math.pow(1 + raiseRate, yearIndex);
  }

  calculateProjections = () => {
    const currentNetWorth = parseFloat(this.state.currentNetWorth);
    const growthRate = parseFloat(this.state.growthRate) / 100;
    const annualRaiseRate = parseFloat(this.state.annualRaiseRate) / 100;
    const baseSavings = this.getMonthlySavings();

    if (isNaN(currentNetWorth)) {
      alert('Please enter a valid current net worth');
      return;
    }
    if (isNaN(growthRate)) {
      alert('Please enter a valid growth rate');
      return;
    }
    if (isNaN(annualRaiseRate)) {
      alert('Please enter a valid annual raise rate');
      return;
    }

    const monthlyRate = growthRate / 12;
    const projections = [];
    let balance = currentNetWorth;

    for (let year = 1; year <= 40; year++) {
      for (let month = 0; month < 12; month++) {
        const monthIndex = (year - 1) * 12 + month;
        const monthlySavings = this.getMonthlySavingsForMonth(monthIndex, baseSavings, annualRaiseRate);
        balance = balance * (1 + monthlyRate) + monthlySavings;
      }
      projections.push({ year, netWorth: balance });
    }

    this.setState({ projections });
  }

  formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }

  render() {
    return (
      <div className="NetWorthProjection-container">
        <Link to="/ntwrth" className="NetWorthProjection-back-link">
          ← Back to Net Worth
        </Link>

        <Typography variant="h5" component="h1" className="NetWorthProjection-title">
          Net Worth Projection
        </Typography>

        <Card raised className="NetWorthProjection-form-card" elevation={2}>
          <TextField
            label="Current Net Worth"
            type="number"
            value={this.state.currentNetWorth}
            onChange={this.handleInputChange('currentNetWorth')}
            variant="outlined"
            fullWidth
            className="NetWorthProjection-field"
          />

          <TextField
            label="Real Annual Growth Rate (%)"
            type="number"
            value={this.state.growthRate}
            onChange={this.handleInputChange('growthRate')}
            variant="outlined"
            fullWidth
            className="NetWorthProjection-field"
            inputProps={{ step: '0.1' }}
          />

          <TextField
            label="Annual Raise on Savings (%)"
            type="number"
            value={this.state.annualRaiseRate}
            onChange={this.handleInputChange('annualRaiseRate')}
            variant="outlined"
            fullWidth
            className="NetWorthProjection-field"
            inputProps={{ step: '0.1' }}
          />

          <TextField
            label="Monthly Savings"
            type="number"
            value={this.state.monthlySavings}
            onChange={this.handleInputChange('monthlySavings')}
            variant="outlined"
            fullWidth
            className="NetWorthProjection-field NetWorthProjection-field--last"
          />

          <Button
            variant="contained"
            color="primary"
            onClick={this.calculateProjections}
            fullWidth
            className="NetWorthProjection-submit-button"
          >
            Submit
          </Button>
        </Card>

        {this.state.projections && (
          <Card raised className="NetWorthProjection-results-card" elevation={2}>
            <Typography variant="h6" component="h2" className="NetWorthProjection-results-title">
              Projections
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Year</TableCell>
                    <TableCell align="right">Projected Net Worth</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {this.state.projections.map((row) => (
                    <TableRow key={row.year}>
                      <TableCell>{row.year}</TableCell>
                      <TableCell align="right">{this.formatCurrency(row.netWorth)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  financialAccounts: state.reducer.financialAccounts
});

export default connect(mapStateToProps)(NetWorthProjection);
